import React from 'react';
import { motion } from 'framer-motion';
import {MapPin, Car, DollarSign, Clock, Ticket} from 'lucide-react';
import { useScrollAnimation, useParallax } from '../../hooks/useScrollAnimation';

export default function Orlando() {
  const scrollY = useParallax();
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation(0.2);
  const { ref: parksRef, isVisible: parksVisible } = useScrollAnimation(0.2);
  const { ref: attractionsRef, isVisible: attractionsVisible } = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero with Parallax */}
      <div className="relative h-[500px] overflow-hidden">
        <motion.img
          src="https://images.pexels.com/photos/358312/pexels-photo-358312.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Orlando"
          className="w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-transparent" />
        <motion.div 
          className="absolute inset-0 flex items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1 
              className="text-6xl font-bold text-white mb-3"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Orlando, Florida
            </motion.h1>
            <motion.p 
              className="text-2xl text-white/90"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Theme Park Capital of the World
            </motion.p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div 
          ref={infoRef}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={infoVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {[
            { icon: Clock, color: 'text-[#4A90E2]', label: 'Recommended Stay', value: '5-7 days' },
            { icon: DollarSign, color: 'text-[#F7B32B]', label: 'Daily Budget', value: '$150-300' },
            { icon: Car, color: 'text-[#FF6B35]', label: 'Best Transport', value: 'Rental Car' },
            { icon: Ticket, color: 'text-[#0A2540]', label: 'Weather', value: 'Sunny Year-Round' }
          ].map((item, idx) => (
            <motion.div 
              key={idx}
              className="bg-white rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              initial={{ opacity: 0, y: 20 }}
              animate={infoVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <item.icon className={`w-6 h-6 ${item.color} mb-2`} />
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="font-semibold text-gray-800">{item.value}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">About Orlando</h2>
            <p className="text-gray-600 mb-4">
              Orlando is home to Walt Disney World, Universal Orlando Resort, SeaWorld, and countless other attractions. It's the ultimate destination for families, thrill-seekers, and anyone who wants to experience world-class theme park entertainment.
            </p>
            <p className="text-gray-600 mb-8">
              With year-round sunshine, exceptional hotels, diverse dining, and attractions for all ages, Orlando delivers magical experiences whether you're 5 or 95.
            </p>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Major Theme Parks</h3>
            <motion.div 
              ref={parksRef}
              className="space-y-4 mb-8"
            >
              {[
                { name: 'Walt Disney World', desc: '4 parks: Magic Kingdom, EPCOT, Hollywood Studios, Animal Kingdom' },
                { name: 'Universal Orlando Resort', desc: 'Universal Studios, Islands of Adventure, Volcano Bay water park' },
                { name: 'SeaWorld Orlando', desc: 'Marine life park with thrilling coasters' },
                { name: 'LEGOLAND Florida', desc: 'Perfect for families with young children (45 min drive)' }
              ].map((park, idx) => (
                <motion.div 
                  key={idx} 
                  className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF6B35] hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, x: -30 }}
                  animate={parksVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ x: 10 }}
                >
                  <h4 className="font-semibold text-gray-800 mb-1">{park.name}</h4>
                  <p className="text-sm text-gray-600">{park.desc}</p>
                </motion.div>
              ))}
            </motion.div>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Top Attractions</h3>
            <motion.ul 
              ref={attractionsRef}
              className="space-y-2 text-gray-700"
            >
              {[
                { name: 'Wizarding World of Harry Potter', desc: 'Immersive themed land at Universal' },
                { name: 'Cinderella Castle', desc: 'Iconic centerpiece of Magic Kingdom' },
                { name: 'Star Wars: Galaxy\'s Edge', desc: 'Live your Star Wars adventure at Hollywood Studios' },
                { name: 'Pandora - World of Avatar', desc: 'Breathtaking alien world at Animal Kingdom' },
                { name: 'Kennedy Space Center', desc: 'NASA\'s launch headquarters (1 hour drive)' }
              ].map((attraction, idx) => (
                <motion.li 
                  key={idx}
                  className="flex items-start hover:bg-white hover:shadow-sm p-2 rounded transition-all duration-200"
                  initial={{ opacity: 0, x: -20 }}
                  animate={attractionsVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                >
                  <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>{attraction.name}:</strong> {attraction.desc}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="bg-gradient-to-br from-[#FF6B35] to-[#0A2540] rounded-xl p-6 text-white shadow-lg sticky top-4">
              <h3 className="text-2xl font-bold mb-4">Planning Tips</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">🎟️</span>
                  <span>Buy multi-day tickets online for best value</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">⚡</span>
                  <span>Get Express Pass at Universal to skip lines</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏰</span>
                  <span>Book Disney Genie+ for Lightning Lane access</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🌤️</span>
                  <span>Avoid summer heat - visit Jan-Feb or Sep-Nov</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏨</span>
                  <span>Stay on-site for early park entry benefits</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}